import {
  downloadExcel,
  getTable,
  postRequest,
  getRequest,
  putRequest,
  deleteRequest,
  postDownloadExcel,
} from "./requestTemplates";
import { Graph_Dto } from "src/Interfaces/orderbookGraphInterfaces";
import { Packet } from "../Interfaces/commonDtos";
import { Report } from "../Interfaces/TableInterfaces";
import { DropdownItem } from "../Interfaces/dropdownInterfaces";
import type { User } from "src/Interfaces/connections";
import type {
  IForbiddenConstraintSearchParams,
  IForbiddenConstraintTableReport,
  IIsinsGroupSearchParams,
  IAddIsinsGroupResult,
  IPortfolioGroupSearchParams,
  IPortfolioGroupTableReport,
  IUpsertPortfolioGroup,
  IAggregateUserSearchParams,
  IAggregateUserTableReport,
  IUserPortfoGroupSearchParams,
  IUserPortfoGroupTableReport,
  IUserPortfoGroupSelectionRow,
  IUpsertUserPortfoGroup,
  IAssetLimitSearchParams,
  IAssetLimitTableReport,
  ILimitItemSearchParams,
  ILimitItemTableReport,
  IPortfoGroupAllocationSearchParams,
  IPortfoGroupAllocationTableReport,
  IPortfoGroupBuyingPowerSummarySearchParams,
  IPortfoGroupBuyingPowerSummaryTableReport,
  IPortfoGroupInventorySearchParams,
  IPortfoGroupInventoryTableReport,
  IPortfoGroupCoveredCallSearchParams,
  IPortfoGroupCoveredCallTableReport,
  IPortfoCoverageSearchParams,
  IPortfoCoverageTableReport,
  IPortfoImbalanceSummarySearchParams,
  IPortfoImbalanceSummaryTableReport,
  IPortfoLimitDeviationSummarySearchParams,
  IPortfoLimitDeviationSummaryTableReport,
  IPortfoCoverageDetailSearchParams,
  IPortfoCoverageDetailTableReport,
  IAddLimitItemParams,
  IPortfoSegregationSummarySearchParams,
  IPortfoSegregationSummaryTableReport,
  IPortfoSegregationSummaryTableResponse,
  IPortfoSegregationSearchParams,
  IPortfoSegregationTableReport,
  IPortfoSegregationDetailsSearchParams,
  IPortfoSegregationDetailsTableReport,
  IUserAssign,
  IUserAssignPreview,
  IPortfoGroupDetailedSearchParams,
  IStrategyPortfolioSearchParams,
  INakedOptionPortfolioSearchParams,
  IPortfoOverviewTableReport,
  IPortfoOverviewTableResponse,
  ISecuritiesBuyingPowerTableReport,
  ICoveredCallAllocationTableReport,
  ICoveredCallSummaryTableReport,
  IDeltaHedgeSummaryTableReport,
  IConversionSummaryTableReport,
  IConversionAllocationTableReport,
  INonOptionAssetClassPortfolioTableReport,
  INakedOptionPortfolioTableReport,
  ICoveredCallPortfolioTableReport,
  IConversionPortfolioTableReport,
  IUpsertUserAllowedCreditPercent,
} from "src/Interfaces/portfolioGroups";

//-------------------------------تعریف گروه پرتفو--------------------------------
/**
 * portfolio group => Definition
 * جدول محدودیت‌ها
 *
 * @param {IForbiddenConstraintSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getForbiddenConstraintTable = (
  searchParam: IForbiddenConstraintSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IForbiddenConstraintTableReport>> =>
  getTable<IForbiddenConstraintSearchParams, IForbiddenConstraintTableReport>(
    "/v1/portfogroup/forbiddenconstraint/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Definition
 * افزودن گروهی نماد
 *
 * @param {IIsinsGroupSearchParams} params
 * @async
 * @version 1.4.0
 */
export const addIsinsGroup = (
  params: IIsinsGroupSearchParams,
): Promise<Packet<IAddIsinsGroupResult>> =>
  postRequest("/v1/portfogroup/limititem/groupadd", params);

/**
 * portfolio group => Definition
 * دراپ داون گروه پرتفو
 *
 * @async
 * @version 1.4.0
 */
export const getPortfolioGroupDropDown = (): Promise<DropdownItem[]> =>
  getRequest("/v1/portfogroup/dropdown");

export const getPortfolioGroupAccountsDropdown = (): Promise<User[]> =>
  getRequest("/v1/portfogroup/accounts/dropdown");

export const getPortfolioGroupTabs = (): Promise<DropdownItem[]> =>
  getRequest("/v1/portfogroup/tabs");

/**
 * portfolio group => Definition
 * دراپ داون نوع دارایی
 *
 * @async
 * @version 1.4.0
 */
export const getAssetTypeDropDown = (): Promise<DropdownItem[]> =>
  getRequest("/v1/portfogroup/assettype/dropdown");

export const getInventoryAssetTypesDropDown = (): Promise<DropdownItem[]> =>
  getRequest("/v1/portfogroup/inventory/assettypes/dropdown");

export const getAllocationAssetTypesDropDown = (): Promise<DropdownItem[]> =>
  getRequest("/v1/portfogroup/allocation/assettypes/dropdown");

/**
 * portfolio group => Definition
 * دراپ داون نماد
 *
 * @async
 * @version 1.4.0
 */
export const getInstrumentsDropDown = (
  assetType?: string | number | null,
): Promise<DropdownItem[]> =>
  assetType
    ? getRequest(
        `/v1/portfogroup/instruments/byassettype?assetType=${assetType}`,
      )
    : Promise.resolve([]);

export const getPortfolioGroupInstrumentDropdown = (
  includeOptions = true,
): Promise<DropdownItem[]> =>
  getRequest(`/v1/portfogroup/instrumentoptions?includeOptions=${includeOptions}`);

/**
 * portfolio group => Definition
 * جدول اضافه کردن جدول پرتفو
 *
 * @param {IForbiddenConstraintSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getPortfolioGroupTable = (
  searchParam: IPortfolioGroupSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IPortfolioGroupTableReport>> =>
  getTable<IPortfolioGroupSearchParams, IPortfolioGroupTableReport>(
    "/v1/portfogroup/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Definition
 * افزودن و ویرایش گروه
 *
 * @param {IIsinsGroupSearchParams} params
 * @async
 * @version 1.4.0
 */
export const upsertPortfolioGroup = (
  params: IUpsertPortfolioGroup,
): Promise<Packet<any>> => postRequest("/v1/portfogroup/upsert", params);

/**
 * portfolio group => Definition
 * حذف گروه
 *
 * @param {number} id
 * @async
 * @version 1.4.0
 */
export const deletePortfolioGroup = (id: number): Promise<Packet<boolean>> =>
  postRequest(`/v1/portfogroup/delete`, { id });

/**
 * portfolio group => Definition
 * "جدول تنظیم انواع دارایی های گروه پرتفو"
 *
 * @param {IAssetLimitSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getPortfoGroupAssetLimit = (
  searchParam: IAssetLimitSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IAssetLimitTableReport>> =>
  getTable<IAssetLimitSearchParams, IAssetLimitTableReport>(
    "/v1/portfogroup/assetlimit/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Definition
 * جدول محدودیت نماد گروه پرتفو
 *
 * @param {ILimitItemSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getPortfoGroupLimitItem = (
  searchParam: ILimitItemSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<ILimitItemTableReport>> =>
  getTable<ILimitItemSearchParams, ILimitItemTableReport>(
    "/v1/portfogroup/limititem/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Definition
 * ویرایش محدودیت نوع دارایی
 *
 * @param {object} params
 * @async
 * @version 1.4.0
 */
export const editPortfoGroupAssetLimit = (params: {
  PortfoGroupAssetLimitId: number;
  MinValue: number;
  MaxValue: number;
}): Promise<Packet<any>> =>
  postRequest("/v1/portfogroup/assetlimit/edit", params);

/**
 * portfolio group => Definition
 * افزودن محدودیت نماد
 *
 * @param {object} IAddLimitItemParams
 * @async
 * @version 1.4.0
 */
export const addPortfoGroupLimitItem = (
  params: IAddLimitItemParams,
): Promise<Packet<any>> => postRequest("/v1/portfogroup/limititem/add", params);
/**
 * portfolio group => Definition
 * ویرایش محدودیت نماد
 *
 * @param {object} params
 * @async
 * @version 1.4.0
 */
export const editPortfoGroupLimitItem = (params: {
  Id: number;
  MinValue: number;
  MaxValue: number;
}): Promise<Packet<any>> =>
  postRequest("/v1/portfogroup/limititem/edit", params);

/**
 * portfolio group => Definition
 * حذف محدودیت نماد
 *
 * @param {object} params
 * @async
 * @version 1.4.0
 */
export const deletePortfoGroupLimitItem = (params: {
  Isin: string;
  PortfoGroupIds: number[];
  assetType: number;
}): Promise<Packet<any>> =>
  postRequest("/v1/portfogroup/limititem/delete", params);

//------------------------------------تخصیص اکانت--------------------------------

/**
 * portfolio group => Account Assignment
 * جدول تعداد اکانت های گروه پرتفو
 *
 * @param {IAggregateUserSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const aggregateUserPortfoGroup = (
  searchParam: IAggregateUserSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IAggregateUserTableReport>> =>
  getTable<IAggregateUserSearchParams, IAggregateUserTableReport>(
    "/v1/portfogroup/aggregateuser/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Account Assignment
 * جدول کاربران گروه پرتفو
 *
 * @param {IUserPortfoGroupSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getUserPortfoGroupTable = (
  searchParam: IUserPortfoGroupSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IUserPortfoGroupTableReport>> =>
  getTable<IUserPortfoGroupSearchParams, IUserPortfoGroupTableReport>(
    "/v1/portfogroup/user/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getUserPortfoGroupExcel = (
  searchParam: IUserPortfoGroupSearchParams,
) =>
  postDownloadExcel(
    `/v1/portfogroup/user/excel?${Math.random()}`,
    searchParam,
  );

export const getUserPortfoGroupSelectionRows = (): Promise<
  IUserPortfoGroupSelectionRow[]
> =>
  getUserPortfoGroupTable(
    {
      sahraUserId: null,
      portfolioGroupId: null,
    },
    0,
    5000,
  ).then((res) => res.report as unknown as IUserPortfoGroupSelectionRow[]);

/**
 * portfolio group => Account Assignment
 *  دکمه تخصیص اکانت
 *
 * @param {IUpsertUserPortfoGroup} params
 * @async
 * @version 1.4.0
 */
export const setUserAssign = (params: IUserAssign): Promise<Packet<any>> =>
  postRequest("/v1/portfogroup/user/bulkassign", params);

export const previewUserAssign = (
  params: IUserAssign,
): Promise<Packet<IUserAssignPreview>> =>
  postRequest("/v1/portfogroup/user/bulkassign/preview", params);

/**
 * portfolio group => Account Assignment
 * افزودن / ویرایش تخصیص اکانت
 *
 * @param {IUpsertUserPortfoGroup} params
 * @async
 * @version 1.4.0
 */
export const upsertUserPortfoGroup = (
  params: IUpsertUserPortfoGroup,
): Promise<Packet<any>> => postRequest("/v1/portfogroup/user/upsert", params);

/**
 * portfolio group => Status Summary
 * جدول وضعیت نوع دارایی پرتفو کاربران
 *
 * @param {IPortfoGroupAllocationSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getPortfoGroupAllocationTable = (
  searchParam: IPortfoGroupAllocationSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IPortfoGroupAllocationTableReport>> =>
  getTable<
    IPortfoGroupAllocationSearchParams,
    IPortfoGroupAllocationTableReport
  >(
    "/v1/portfogroup/allocation/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Inventory Summary
 * جدول خلاصه موجودی
 *
 * @param {IPortfoGroupInventorySearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 */
export const getPortfoGroupInventoryTable = (
  searchParam: IPortfoGroupInventorySearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IPortfoGroupInventoryTableReport>> =>
  getTable<IPortfoGroupInventorySearchParams, IPortfoGroupInventoryTableReport>(
    "/v1/portfogroup/inventory/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Inventory Summary
 * دانلود اکسل خلاصه موجودی
 *
 * @param {IPortfoGroupInventorySearchParams} searchParam
 * @async
 */
export const getPortfoGroupInventoryExcel = (
  searchParam: IPortfoGroupInventorySearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/inventory/excel?${rand}`,
    searchParam,
  );
};

/**
 * portfolio group => Covered Call Summary
 * جدول خلاصه کاوردکال
 *
 * @param {IPortfoGroupCoveredCallSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 */
export const getPortfoGroupCoveredCallTable = (
  searchParam: IPortfoGroupCoveredCallSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IPortfoGroupCoveredCallTableReport>> =>
  getTable<
    IPortfoGroupCoveredCallSearchParams,
    IPortfoGroupCoveredCallTableReport
  >(
    "/v1/portfogroup/coveredcall/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Covered Call Summary
 * دانلود اکسل خلاصه کاوردکال
 *
 * @param {IPortfoGroupCoveredCallSearchParams} searchParam
 * @async
 */
export const getPortfoGroupCoveredCallExcel = (
  searchParam: IPortfoGroupCoveredCallSearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/coveredcall/excel?${rand}`,
    searchParam,
  );
};

/**
 * portfolio group => Coverage Summary
 * جدول پوشش پرتفو
 */
export const getPortfoCoverageTable = (
  searchParam: IPortfoCoverageSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IPortfoCoverageTableReport>> =>
  getTable<IPortfoCoverageSearchParams, IPortfoCoverageTableReport>(
    "/v1/portfogroup/portfocoverage/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Coverage Summary
 * دانلود اکسل پوشش پرتفو
 */
export const getPortfoCoverageExcel = (
  searchParam: IPortfoCoverageSearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/portfocoverage/excel?${rand}`,
    searchParam,
  );
};

/**
 * portfolio group => Imbalance Summary
 * جدول خلاصه ناترازی
 */
export const getPortfoImbalanceSummaryTable = (
  searchParam: IPortfoImbalanceSummarySearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IPortfoImbalanceSummaryTableReport>> =>
  getTable<
    IPortfoImbalanceSummarySearchParams,
    IPortfoImbalanceSummaryTableReport
  >(
    "/v1/portfogroup/portfoimbalancesummary/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Imbalance Summary
 * دانلود اکسل خلاصه ناترازی
 */
export const getPortfoImbalanceSummaryExcel = (
  searchParam: IPortfoImbalanceSummarySearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/portfoimbalancesummary/excel?${rand}`,
    searchParam,
  );
};

/**
 * portfolio group => Limit deviation summary
 * جدول انحراف از سقف/کف
 */
export const getPortfoLimitDeviationSummaryTable = (
  searchParam: IPortfoLimitDeviationSummarySearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IPortfoLimitDeviationSummaryTableReport>> =>
  getTable<
    IPortfoLimitDeviationSummarySearchParams,
    IPortfoLimitDeviationSummaryTableReport
  >(
    "/v1/portfogroup/portfolimitdeviationsummary/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Limit deviation summary
 * دانلود اکسل انحراف از سقف/کف
 */
export const getPortfoLimitDeviationSummaryExcel = (
  searchParam: IPortfoLimitDeviationSummarySearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/portfolimitdeviationsummary/excel?${rand}`,
    searchParam,
  );
};

export const getPortfoLimitDeviationAssetTypesDropdown = (): Promise<
  DropdownItem[]
> =>
  getRequest(
    "/v1/portfogroup/portfolimitdeviationsummary/assettypes/dropdown",
  );

/**
 * portfolio group => Coverage Summary
 * جدول جزئیات پوشش پرتفو
 */
export const getPortfoCoverageDetailTable = (
  searchParam: IPortfoCoverageDetailSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IPortfoCoverageDetailTableReport>> =>
  getTable<IPortfoCoverageDetailSearchParams, IPortfoCoverageDetailTableReport>(
    "/v1/portfogroup/portfocoveragedetail/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Assignment Details
 * خلاصه تفکیک پرتفو کاربر
 *
 * @param {IPortfoSegregationSummarySearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getPortfoSegregationSummaryTable = (
  searchParam: IPortfoSegregationSummarySearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<IPortfoSegregationSummaryTableResponse> =>
  getTable<
    IPortfoSegregationSummarySearchParams,
    IPortfoSegregationSummaryTableReport
  >(
    "/v1/portfogroup/segregationsummary/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Assignment Details
 * دانلود اکسل خلاصه تفکیک پرتفو کاربر
 *
 * @param {IPortfoSegregationSummarySearchParams} searchParam
 * @async
 * @version 1.4.0
 */
export const getPortfoSegregationSummaryExcel = (
  searchParam: IPortfoSegregationSummarySearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/segregationsummary/excel?${rand}`,
    searchParam,
  );
};

/**
 * portfolio group => Assignment Details
 * تفکیک پرتفو کاربر
 *
 * @param {IPortfoSegregationSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getPortfoSegregationTable = (
  searchParam: IPortfoSegregationSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IPortfoSegregationTableReport>> =>
  getTable<IPortfoSegregationSearchParams, IPortfoSegregationTableReport>(
    "/v1/portfogroup/segregation/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Assignment Details
 * دانلود اکسل تفکیک پرتفو کاربر
 *
 * @param {IPortfoSegregationSearchParams} searchParam
 * @async
 * @version 1.4.0
 */
export const getPortfoSegregationExcel = (
  searchParam: IPortfoSegregationSearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/segregation/excel?${rand}`,
    searchParam,
  );
};

/**
 * portfolio group => Assignment Details
 * جزئیات پرتفو کاربر
 *
 * @param {IPortfoSegregationDetailsSearchParams} searchParam
 * @param {number | null} offset
 * @param {number | null} pageSize
 * @param {string} sortingType
 * @async
 * @version 1.4.0
 */
export const getPortfoSegregationDetailsTable = (
  searchParam: IPortfoSegregationDetailsSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IPortfoSegregationDetailsTableReport>> =>
  getTable<
    IPortfoSegregationDetailsSearchParams,
    IPortfoSegregationDetailsTableReport
  >(
    "/v1/portfogroup/segregation/detailtable",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Detailed Portfolio => Delta Hedge details
 */
export const getDeltaHedgeSegregationDetailsTable = (
  searchParam: IPortfoSegregationDetailsSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IPortfoSegregationDetailsTableReport>> =>
  getTable<
    IPortfoSegregationDetailsSearchParams,
    IPortfoSegregationDetailsTableReport
  >(
    "/v1/portfogroup/segregation/deltahedge/detailtable",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

/**
 * portfolio group => Inventory Summary
 * دانلود اکسل خلاصه وضعیت
 *
 * @param {IPortfoGroupInventorySearchParams} searchParam
 * @async
 */
export const getPortfoGroupallocationExcel = (
  searchParam: IPortfoGroupAllocationSearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/allocation/excel?${rand}`,
    searchParam,
  );
};

//------------------------------------پرتفوی جزئی--------------------------------

export const getPortfoGroupBuyingPowerSummaryTable = (
  searchParam: IPortfoGroupBuyingPowerSummarySearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IPortfoGroupBuyingPowerSummaryTableReport>> =>
  getTable<
    IPortfoGroupBuyingPowerSummarySearchParams,
    IPortfoGroupBuyingPowerSummaryTableReport
  >(
    "/v1/portfogroup/securitiesbuyingpower/summary/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getPortfoGroupBuyingPowerSummaryExcel = (
  searchParam: IPortfoGroupBuyingPowerSummarySearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/securitiesbuyingpower/summary/excel?${rand}`,
    searchParam,
  );
};

export const getPortfoOverviewTable = (
  searchParam: IPortfoGroupDetailedSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<IPortfoOverviewTableResponse> =>
  getTable<IPortfoGroupDetailedSearchParams, IPortfoOverviewTableReport>(
    "/v1/portfogroup/overview/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getSecuritiesBuyingPowerTable = (
  searchParam: IPortfoGroupDetailedSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<ISecuritiesBuyingPowerTableReport>> =>
  getTable<IPortfoGroupDetailedSearchParams, ISecuritiesBuyingPowerTableReport>(
    "/v1/portfogroup/securitiesbuyingpower/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getCoveredCallSummaryTable = (
  searchParam: IPortfoGroupDetailedSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<ICoveredCallSummaryTableReport>> =>
  getTable<IPortfoGroupDetailedSearchParams, ICoveredCallSummaryTableReport>(
    "/v1/portfogroup/coveredcallsummary/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getCoveredCallSummaryExcel = (
  searchParam: IPortfoGroupDetailedSearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/coveredcallsummary/excel?${rand}`,
    searchParam,
  );
};

export const getCoveredCallAllocationTable = (
  searchParam: IPortfoGroupDetailedSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<ICoveredCallAllocationTableReport>> =>
  getTable<
    IPortfoGroupDetailedSearchParams,
    ICoveredCallAllocationTableReport
  >(
    "/v1/portfogroup/coveredcallallocation/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getCoveredCallAllocationExcel = (
  searchParam: IPortfoGroupDetailedSearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/coveredcallallocation/excel?${rand}`,
    searchParam,
  );
};

export const getDeltaHedgeSummaryTable = (
  searchParam: IPortfoGroupDetailedSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IDeltaHedgeSummaryTableReport>> =>
  getTable<IPortfoGroupDetailedSearchParams, IDeltaHedgeSummaryTableReport>(
    "/v1/portfogroup/deltahedgesummary/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getConversionSummaryTable = (
  searchParam: IPortfoGroupDetailedSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IConversionSummaryTableReport>> =>
  getTable<IPortfoGroupDetailedSearchParams, IConversionSummaryTableReport>(
    "/v1/portfogroup/conversionsummary/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getConversionSummaryExcel = (
  searchParam: IPortfoGroupDetailedSearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/conversionsummary/excel?${rand}`,
    searchParam,
  );
};

export const getConversionAllocationTable = (
  searchParam: IPortfoGroupDetailedSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IConversionAllocationTableReport>> =>
  getTable<
    IPortfoGroupDetailedSearchParams,
    IConversionAllocationTableReport
  >(
    "/v1/portfogroup/conversionallocation/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getConversionAllocationExcel = (
  searchParam: IPortfoGroupDetailedSearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/conversionallocation/excel?${rand}`,
    searchParam,
  );
};

export const getNonOptionAssetClassPortfolioTable = (
  searchParam: IPortfoGroupDetailedSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<INonOptionAssetClassPortfolioTableReport>> =>
  getTable<
    IPortfoGroupDetailedSearchParams,
    INonOptionAssetClassPortfolioTableReport
  >(
    "/v1/portfogroup/nonoptionassetclassportfolio/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getNakedOptionPortfolioTable = (
  searchParam: INakedOptionPortfolioSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<INakedOptionPortfolioTableReport>> =>
  getTable<INakedOptionPortfolioSearchParams, INakedOptionPortfolioTableReport>(
    "/v1/portfogroup/nakedoptionportfolio/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getNakedOptionPortfolioExcel = (
  searchParam: INakedOptionPortfolioSearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/nakedoptionportfolio/excel?${rand}`,
    searchParam,
  );
};

export const getCoveredCallPortfolioTable = (
  searchParam: IStrategyPortfolioSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<ICoveredCallPortfolioTableReport>> =>
  getTable<IStrategyPortfolioSearchParams, ICoveredCallPortfolioTableReport>(
    "/v1/portfogroup/coveredcallportfolio/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getConversionPortfolioTable = (
  searchParam: IStrategyPortfolioSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IConversionPortfolioTableReport>> =>
  getTable<IStrategyPortfolioSearchParams, IConversionPortfolioTableReport>(
    "/v1/portfogroup/conversionportfolio/table",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const upsertUserAllowedCreditPercent = (
  params: IUpsertUserAllowedCreditPercent,
): Promise<Packet<boolean>> =>
  postRequest("/v1/portfogroup/user/allowedcreditpercent/upsert", params);

export const upsertGroupingSettingsUserAllowedCreditPercent = (
  params: IUpsertUserAllowedCreditPercent,
): Promise<Packet<boolean>> =>
  postRequest(
    "/v1/portfogroup/groupingsettings/user/allowedcreditpercent/upsert",
    params,
  );

export const getDetailedPortfolioExcel = (
  searchParam: IPortfoGroupDetailedSearchParams,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/portfogroup/detailedportfolio/excel?${rand}`,
    searchParam,
  );
};
